// UPost - W-9 Tax Form Collection
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Logo } from '../components/Logo';
import { ArrowLeft, Shield, CheckCircle } from 'lucide-react';

export function W9Form() {
  const [formData, setFormData] = useState({
    legal_name: '',
    business_name: '',
    tax_classification: 'individual',
    ssn_ein: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    signature: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.request('/referrals/submit-w9', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      setSuccess(true);
      setTimeout(() => navigate('/referrals'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to submit W-9 form');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="mx-auto text-green-600 mb-4" size={64} />
            <h2 className="text-2xl font-bold mb-2">W-9 Submitted Successfully!</h2>
            <p className="text-gray-600 mb-4">
              You can now withdraw your commission earnings.
            </p>
            <p className="text-sm text-gray-500">Redirecting...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Link to="/">
              <Logo variant="full" size="md" />
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">{user?.email}</span>
              <Link to="/referrals">
                <Button variant="ghost" size="sm">
                  <ArrowLeft size={16} className="mr-2" />
                  Back
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                Log Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Info Banner */}
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Shield className="text-blue-600" size={20} />
          <AlertDescription className="ml-2">
            <strong>IRS Form W-9 Required</strong>
            <p className="text-sm mt-1">
              U.S. tax law requires us to collect this information before paying commissions. 
              Your data is encrypted and securely stored. We'll use it to generate 1099-NEC 
              forms if you earn $600+ in a calendar year.
            </p>
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Submit Your W-9 Information</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="mb-6 bg-red-50 border-red-200">
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Legal Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Legal Name (as shown on tax return) *
                </label>
                <Input
                  name="legal_name"
                  value={formData.legal_name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Business Name (if different from legal name)
                </label>
                <Input
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </div>

              {/* Tax Classification */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tax Classification *
                </label>
                <select
                  name="tax_classification"
                  value={formData.tax_classification}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="individual">Individual/Sole Proprietor</option>
                  <option value="llc">LLC</option>
                  <option value="corporation">C Corporation</option>
                  <option value="partnership">Partnership</option>
                  <option value="trust">Trust/Estate</option>
                </select>
              </div>

              {/* SSN or EIN */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Social Security Number (SSN) or Employer ID Number (EIN) *
                </label>
                <Input
                  name="ssn_ein"
                  type="password"
                  value={formData.ssn_ein}
                  onChange={handleChange}
                  required
                  placeholder="XXX-XX-XXXX or XX-XXXXXXX"
                  maxLength={11}
                />
                <p className="text-xs text-gray-500 mt-1">
                  🔒 Encrypted and stored securely
                </p>
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Address Line 1 *
                </label>
                <Input
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                  required
                  placeholder="123 Main Street"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Address Line 2
                </label>
                <Input
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleChange}
                  placeholder="Apt 4B (Optional)"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">City *</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">State *</label>
                  <Input
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    placeholder="NY"
                    maxLength={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">ZIP Code *</label>
                  <Input
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleChange}
                    required
                    placeholder="10001"
                    maxLength={10}
                  />
                </div>
              </div>

              {/* Digital Signature */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Digital Signature (Type your full name) *
                </label>
                <Input
                  name="signature"
                  value={formData.signature}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
                <p className="text-xs text-gray-500 mt-1">
                  By typing your name, you certify that the information provided is correct.
                </p>
              </div>

              {/* Certification Statement */}
              <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700">
                <p className="font-medium mb-2">Certification:</p>
                <p className="text-xs leading-relaxed">
                  Under penalties of perjury, I certify that: (1) The number shown on this form 
                  is my correct taxpayer identification number, and (2) I am not subject to backup 
                  withholding because: (a) I am exempt from backup withholding, or (b) I have not 
                  been notified by the IRS that I am subject to backup withholding.
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/referrals')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Submitting...' : 'Submit W-9 Form'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>🔒 All data is encrypted using bank-level security</p>
          <p className="mt-1">Your information will only be used for tax reporting purposes</p>
        </div>
      </div>
    </div>
  );
}
